// /compare/honeybook: Pixally vs HoneyBook.
//
// Plain data, no JSX, so pages/ComparePage.jsx (in the browser) and
// scripts/prerender.mjs (at build) render the same words. Body blocks are the
// blog's own types (lib/blogRenderer.jsx) plus one more, 'table', which only the
// compare page renders.
//
// ⛔ THE RULES THIS PAGE LIVES BY (Ryan's GEO brief, 29 Sep 2026):
// - Every HoneyBook fact comes from HoneyBook's own current page, listed in
//   SOURCES with the date it was checked. Re-check them all when you change
//   anything here, and move CHECKED_AT. HoneyBook's evidence, word for word, is in
//   docs/geo/compare-honeybook-draft.md.
// - Every Pixally fact is on the launch feature list (config/planComparison.js,
//   config/pricingPlans.js) or in a published legal page. If the pricing table
//   changes, this page changes in the same commit.
// - No "at launch" labels on individual claims (Ryan, 1 Oct 2026). The launch
//   date is stated where availability is the question: the lede, the
//   "Available" row and the FAQ. ⛔ On January 1, 2027 those become stale and
//   must be updated across every content/compare page.
// - No "the only". Both "better choice" sections are conditional ("if you..."),
//   so neither side is claimed better overall. HoneyBook's section is why an
//   answer engine can cite this page instead of discounting it as spin; ours is
//   the sentence it can quote. Every reason under ours carries a sourced number
//   or a launch-table feature.
// - HoneyBook's pages say nothing about film chapters or embed hosting. Do not
//   fill those gaps with "no". Whether the video hours are per month or a total
//   is NOT on a public page: HoneyBook's support agent answered it in Ryan's
//   account on 1 Oct 2026, and SOURCES attributes it to them. Its first answer
//   said "a total storage limit, not per month" and gave Premium as 54,000 MB;
//   asked again, it said the limit is "by hours, not GB" and "capped by total
//   runtime". Both say total, not monthly, so that is all the page states. Do
//   not put a GB figure for HoneyBook back without a public source.
//
// Two HoneyBook pages disagree on card fees: the pricing page says "Card fees
// start at 2.7% + 10¢" with "No ... premium card brand fees", and the help
// article dated Aug 27, 2026 itemises 2.9% + $0.25 for Visa and Mastercard and
// 3.4% + $0.09 for Amex, Discover and card-on-file payments. This page uses the
// itemised article and links it.

export const ROUTE = '/compare/honeybook';
export const UPDATED_AT = '2026-10-09';
export const CHECKED_AT = 'October 1, 2026';
// The dates line under the lede: "Last updated ... · <CHECKED_LABEL> <CHECKED_AT>".
export const CHECKED_LABEL = 'HoneyBook facts checked';
// The drawing under the title (lib/compareArt.js).
export const HERO_ART = 'versus';

export const TITLE = 'Pixally vs HoneyBook';
export const SEO_TITLE = 'Pixally vs HoneyBook for Wedding Photographers and Videographers';
export const SEO_DESCRIPTION =
  'How Pixally and HoneyBook compare on price, payment fees, galleries, film delivery, second shooters and post-production, with every HoneyBook fact sourced.';

// The answer, first. Answer engines quote the opening of a page more than any
// other part of it.
export const LEDE =
  'HoneyBook is client management software for creative and service businesses of many kinds. Since July 2026 it also delivers photos and videos in its own galleries. Pixally is a CRM built for wedding photographers and videographers, launching on January 1, 2027, with second shooter and contractor management, post-production tracking and film delivery built in.';

const HB_PRICING = 'https://www.honeybook.com/pricing';
const HB_FEES =
  'https://help.honeybook.com/en/articles/15073871-accepted-payment-methods-and-what-processing-fees-apply';
const HB_GALLERIES =
  'https://help.honeybook.com/en/articles/15714707-create-and-share-photo-galleries-in-honeybook';
const HB_ROLES =
  'https://help.honeybook.com/en/articles/6801695-what-permissions-clients-collaborators-and-team-members-have-in-projects';
const HB_LAUNCH = 'https://www.honeybook.com/blog/the-new-honeybook-for-photographers-is-here';

export const BODY = [
  {
    type: 'paragraph',
    text: 'If you work alone and deliver mostly photos, HoneyBook already covers you, and it is available today. If you run a team of second shooters and editors, deliver long films, or take large bank payments, the differences below are worth a look.',
  },
  { type: 'heading', level: 2, text: 'Side by side' },
  {
    type: 'paragraph',
    text: `The HoneyBook column comes from HoneyBook's own published pages, checked ${CHECKED_AT}.`,
  },
  {
    type: 'table',
    label: 'Pixally and HoneyBook compared',
    head: ['', 'Pixally', 'HoneyBook'],
    rows: [
      ['Built for', 'Wedding photographers, videographers and their teams', 'Creatives, consultants, marketers, event professionals and more'],
      ['Available', 'From January 1, 2027', 'Now'],
      ['Plans, billed monthly', 'Free $0, Basic $39, Pro $69, Studio $119', 'Starter $36, Essentials $59, Premium $129'],
      ['Plans, billed yearly (per month)', 'Basic $33, Pro $58, Studio $99', 'Starter $29, Essentials $49, Premium $109'],
      ['Free plan or trial', 'A 14-day free trial with no card, then the Free plan: up to 5 active booked projects', 'No free plan. A 30-day free trial'],
      ['Visa and Mastercard', '2.9% + 30¢ on paid plans (3.4% + 30¢ on Free)', '2.9% + 25¢'],
      ['Amex and Discover', 'Same as other cards', '3.4% + 9¢'],
      ['Saved-card (card on file) payments', 'Same as other cards', '3.4% + 9¢'],
      ['Bank transfer (ACH)', '1% (Free, Basic); 0.8% capped at $12 (Pro) or $8 (Studio)', '1.5%, no cap'],
      ['Photo storage', '200 GB (Basic), 1 TB (Pro), unlimited (Studio)', '200 GB (Starter), unlimited (Essentials, Premium)'],
      ['Video delivery', 'On paid plans: 50 GB included once, then GB packs that never expire; no limit on film length', 'Up to 2, 8 or 15 hours of video by plan, a total for the account rather than per month'],
      ['Website embed hosting for films', '3 films on Free (up to 10 minutes each); 60, 90 or 120 minutes on paid plans, for as long as you subscribe', "Not described on HoneyBook's pages"],
      ['"Powered by" branding on galleries', 'None on any plan', 'Shown on galleries on trial and Starter'],
      ['Team members', '1, 1, 2, unlimited; more for $9/month each', '1, up to 2, unlimited'],
      ['Second shooters and contractors', 'Their own free contractor login and portal: availability, travel fees, tax form, jobs (Pro: up to 10, Studio: unlimited)', 'Added to a project as a "collaborator" with their own HoneyBook login'],
      ['Post-production tracking', 'Built-in post-production management (Pro, Studio)', 'Customizable pipeline and task management'],
      ['More than one brand', '1, 1, 2, unlimited brands', 'Multiple companies on Premium'],
      ['Moving your data in', 'Free migration of your last 12 months of active projects, and free document-to-template conversion', 'Free file setup'],
    ],
  },
  { type: 'heading', level: 2, text: 'When HoneyBook is the better choice' },
  {
    type: 'list',
    items: [
      'It exists today. Pixally launches on January 1, 2027. If you need a CRM this season, HoneyBook is a working product and Pixally is a waitlist.',
      'Its cheapest paid plan costs less. Starter is $29 a month billed yearly, against $33 for Pixally Basic. (Pixally also has a free plan, for up to 5 active booked projects, without gallery storage.)',
      "More photo storage on its middle plan. Essentials includes unlimited photo storage; Pixally's equivalent, Pro, includes 1 TB.",
      'Galleries stay up. HoneyBook says galleries on Essentials and up can stay live for as long as you want. Pixally keeps delivered films live for two years from delivery, and the couple can extend that.',
      'It does more outside the studio. HoneyBook includes AI tools, a business bank account (HoneyBook Balance) and lending (HoneyBook Capital), and connects to Pic-Time if you already deliver there.',
      'A 60-day money-back guarantee.',
    ],
  },
  { type: 'heading', level: 2, text: 'When Pixally is the better choice' },
  {
    type: 'paragraph',
    text: 'Pixally is the better choice if you run a team of second shooters and editors, deliver a lot of film, or take large payments by bank transfer or Amex.',
  },
  { type: 'heading', level: 3, text: 'Second shooters and contractors' },
  { type: 'art', name: 'crew' },
  {
    type: 'paragraph',
    text: `In HoneyBook a second shooter joins a project as a collaborator, with their own HoneyBook company and login, and [HoneyBook's help center](${HB_ROLES}) says every project participant can "view all emails & files sent, and payments made, within the project."`,
  },
  {
    type: 'paragraph',
    text: 'In Pixally a contractor has their own [free contractor account](/contractor-terms) that works across every studio they shoot for. A studio sees their availability, their distance and travel fee for the job, their gear and, once it has booked them, their tax form. Another studio never sees what you pay them or how you rated them. Second shooter and associate management is on Pro (up to 10) and Studio (unlimited), with a contractor portal on both.',
  },
  { type: 'heading', level: 3, text: 'Post-production' },
  { type: 'art', name: 'pipeline' },
  {
    type: 'paragraph',
    text: "HoneyBook gives you a customizable pipeline and tasks, which you shape into an editing workflow yourself. Pixally's post-production management (Pro and Studio) is built around the stages of a wedding edit: uploads, culling and selects, edits assigned to your team or outside editors with due dates, review, client revisions and delivery.",
  },
  { type: 'heading', level: 3, text: 'Films' },
  { type: 'art', name: 'film' },
  {
    type: 'paragraph',
    text: 'HoneyBook delivers video inside its photo galleries, up to a total running time for the whole account: 2, 8 or 15 hours by plan, and 15 hours on Premium ($109 a month billed yearly). Once a studio reaches it, it cannot upload more. Every paid Pixally plan, from Basic ($33 a month billed yearly), starts with 50 GB of video delivery, and more comes in packs that never expire, so there is no ceiling. Pixally also puts no limit on how long a film can be, presents films with chapters so a couple can jump to the ceremony or the first dance, mixes photos and films in one gallery, and hosts your portfolio films for embedding on your own website for as long as you subscribe.',
  },
  { type: 'heading', level: 3, text: 'Payment fees on large payments' },
  { type: 'art', name: 'fees' },
  {
    type: 'paragraph',
    text: 'Wedding retainers and final balances are large, and many couples pay them by bank transfer or with an Amex. On a $3,000 payment:',
  },
  {
    type: 'table',
    label: 'Fees on a $3,000 payment',
    head: ['', 'Pixally', 'HoneyBook'],
    rows: [
      ['Bank transfer', '$30 (Free, Basic); $12 (Pro); $8 (Studio)', '$45'],
      ['Amex or Discover', '$87.30 (paid plans)', '$102.09'],
      ['Visa or Mastercard', '$87.30 (paid plans)', '$87.25'],
    ],
  },
  {
    type: 'paragraph',
    text: 'On Visa and Mastercard the two are effectively the same, and HoneyBook is 5¢ cheaper.',
  },
  { type: 'heading', level: 2, text: 'Switching from HoneyBook' },
  { type: 'art', name: 'switching' },
  {
    type: 'paragraph',
    text: 'Pixally will move your last 12 months of active client projects across for free, and turn your existing documents into Pixally templates. Winter, when the season is quiet, is the easiest time to switch.',
  },
];

export const FAQ = [
  {
    q: 'Does HoneyBook have client galleries?',
    a: 'Yes. Since July 9, 2026, HoneyBook members who are photographers or videographers can deliver photos and videos in HoneyBook galleries, with 200 GB of photo storage on Starter and unlimited on Essentials and Premium.',
  },
  {
    q: 'Can HoneyBook deliver wedding films?',
    a: 'Yes, inside its galleries, up to 2 hours of video on Starter, 8 on Essentials and 15 on Premium. That is a total running time for the account, not a monthly allowance.',
  },
  {
    q: 'Is Pixally cheaper than HoneyBook?',
    a: 'It depends on the plan. Pixally has a free plan and HoneyBook does not. Billed yearly, HoneyBook Starter ($29 a month) costs less than Pixally Basic ($33) and Essentials ($49) less than Pixally Pro ($58), while Pixally Studio ($99) costs less than HoneyBook Premium ($109). Per payment, Pixally costs less on bank transfers on every plan, and on Amex and Discover on paid plans.',
  },
  {
    q: 'Is Pixally available now?',
    a: 'No. Pixally launches on January 1, 2027. You can join the waitlist at pixally.com.',
  },
  {
    q: 'Do my second shooters have to pay for Pixally?',
    a: 'No. Contractor accounts are free.',
  },
  {
    q: 'Can I move my HoneyBook projects to Pixally?',
    a: 'Yes. Migration of your last 12 months of active projects is free on every plan.',
  },
];

export const SOURCES = [
  { label: 'HoneyBook pricing', url: HB_PRICING },
  { label: 'HoneyBook: accepted payment methods and processing fees (dated August 27, 2026)', url: HB_FEES },
  { label: 'HoneyBook: create and share photo galleries', url: HB_GALLERIES },
  { label: 'HoneyBook: what clients, collaborators and team members can do in projects', url: HB_ROLES },
  { label: 'HoneyBook: the new HoneyBook for Photographers (July 9, 2026)', url: HB_LAUNCH },
  { label: 'HoneyBook support, October 1, 2026: the video limit is "capped by total runtime", not per month', url: null },
  { label: 'Pixally plans and fees: the Pixally pricing table', url: null },
  { label: 'Pixally contractor accounts: Contractor Terms of Use, Section 5.1', url: '/contractor-terms' },
];
